import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LimitDialogComponent } from './components/limit-dialog/limit-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from './components/confirm-dialog/confirm-dialog.component';
import {
  ClipboardDialogComponent,
  ClipboardDialogData,
} from './components/clipboard-dialog/clipboard-dialog.component';
import { DialogIds } from './models/dialog-ids';
import { EngineService } from '../engine/engine.service';
import { EventsEnum } from '../engine/models/events.enum';
import { environment } from 'src/environments/environment';
import {
  IframeDialogComponent,
  IframeDialogData,
  IframeMessage,
} from './components/iframe-dialog/iframe-dialog.component';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { capitalize, get, isNil } from 'lodash';
import { Store } from '@ngxs/store';
import { AuthState } from '../auth/state/auth/auth.state';
import { OrganizationsState } from '../organizations/state/organizations.state';
import { PendoService } from '../shared/pendo.service';
import { ContentViewService } from '../shared/content-views.service';
import { UserProfileModel } from '../auth/models/user-profile.model';
import { createAccountSettingsMap } from '../auth/models/account-settings.model';
import { AccountPurposeEnum } from '../account/models/account-purpose.enum';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { OrganizationsService } from '../organizations/organizations.service';
import { OrganizationAttributeModel } from '../organizations/models/organization-attribute.model';

const mobileScreenWidth = 959;
interface PricingMessage {
  plan:
    | 'personal'
    | 'personal-plus'
    | 'class'
    | 'institution'
    | 'starter'
    | 'premium'
    | 'enterprise';
  action: 'try' | 'contact' | 'buy' | 'sign-up';
}

const fullScreenConfig = {
  maxWidth: '100vw',
  width: `100vw`,
  height: `100vh`,
  panelClass: 'full-modal',
};
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  get isMobile(): boolean {
    return window.innerWidth <= mobileScreenWidth;
  }

  constructor(
    private dialog: MatDialog,
    private engineService: EngineService,
    private router: Router,
    private store: Store,
    private pendoService: PendoService,
    private contentViewsService: ContentViewService,
    private organizationsService: OrganizationsService,
  ) {}

  openClipboardDialog(copyText: string, config?: MatDialogConfig<ClipboardDialogData>) {
    const options = {
      data: {
        id: DialogIds.ClipboardDialog,
        copyText: copyText,
      },
      ...config,
    };

    return this.dialog.open(ClipboardDialogComponent, options);
  }

  openConfirmDialog(config?: MatDialogConfig<ConfirmDialogData>) {
    const options = {
      id: DialogIds.ConfirmDialog,
      ...config,
    };

    return this.dialog.open(ConfirmDialogComponent, options);
  }

  openLimitDialog() {
    this.closeAllDialogs();

    return this.dialog.open(LimitDialogComponent, {
      id: DialogIds.LimitDialog,
      width: '480px',
    });
  }

  openUpgradeDialog(closeAll: boolean = true) {
    if (closeAll) {
      this.closeAllDialogs();
    }

    // If web, use the traditional upgrade modal

    // Determine which plan the user currently has.
    // Note that there's currently no way know what business plan they have, so that defaults to null but
    // we presume the business tab

    // Uses https://pricing.biodigital.com
    // Repo: https://github.com/biodigital-human/human-pricing
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);
    const contact = this.getContactProfileParams(profile);

    // Retrieve accountType and subscription to pass to pricing.biodigital.com
    const accountType = profile.premium_account_valid ? profile.premium_account_type : 'free';
    const subscription = get(profile, 'organizations[0].subscription.type', '').toLowerCase();
    const isTrialExpired = this.store.selectSnapshot(OrganizationsState.isTrialExpired);

    // Determine starting pricing page based on user segmentation account purpose
    const accountSettingsMap = createAccountSettingsMap(profile.account_settings);
    const accountPurpose: string = get(accountSettingsMap, 'account_purpose.value', '');

    let page: string;
    switch (accountPurpose) {
      case AccountPurposeEnum.Business:
        page = 'business.html';
        break;
      case AccountPurposeEnum.Education:
        page = 'school.html';
        break;
      default:
        page = 'individual.html';
        break;
    }

    const pricingPageParams = {
      ...contact,
      accountType: accountType,
      subscription: subscription,
      expired: isTrialExpired,
    };

    // When served from mobile, pricing page is included as part of the download so don't use a URL
    const pricingHosts = {
      [EnvironmentDeploy.Local]: `https://localhost:1234`,
      [EnvironmentDeploy.QA]: `https://pricing-qa.biodigital.com`,
      [EnvironmentDeploy.Staging]: `https://pricing.biodigital.com`,
      [EnvironmentDeploy.Production]: `https://pricing.biodigital.com`,
    };
    const pricingHost = environment.native ? '' : pricingHosts[environment.deploy];

    const pageQueryString = this.serializeQueryParams(pricingPageParams);
    // full target url
    const pricingUrl = `${pricingHost}/${page}?${pageQueryString}`;

    // Initialize pricing options for the pricing modal and iframe
    const pricingOptions = {
      id: DialogIds.PricingDialog,
      ...fullScreenConfig,
      data: <IframeDialogData>{
        title: 'Upgrade',
        url: pricingUrl,
        backgroundColor: '#0B1220', // Match pricing page BG
      },
    };

    // Determine returnURL so the user has a way out of upgrading
    const returnUrl = encodeURIComponent(this.router.url);

    // Pop open the pricing iframe dialog and respond to any message it returns
    return this.dialog
      .open(IframeDialogComponent, pricingOptions)
      .afterClosed()
      .subscribe((data: IframeMessage) => {
        if (data) {
          const metadata = {
            action: '',
            plan: '',
            ...data.metadata,
          };

          const plans = {
            business: ['starter', 'premium', 'enterprise'],
            education: ['class', 'institution'],
          };

          // Track pendo event - include user action and plan type
          let planPage = 'Individual';
          Object.entries(plans).forEach(([group, values]) => {
            if (values.indexOf(metadata.plan)) {
              planPage = group;
            }
          });

          if (metadata.action) {
            this.pendoService.track(
              `Pricing - ${capitalize(metadata.action)} - ${capitalize(metadata.plan)}`,
              {
                platform: this.contentViewsService.getProductType(),
                page: planPage,
              },
            );
          }

          switch (metadata.action) {
            case 'buy':
              // If native, bypass the iframe upgrade path and let the app handle it
              if (environment.native) {
                this.engineService.fireEvent(EventsEnum.NativeUpgrade, null);
                return;
              }

              this.router.navigate(['/account/profile'], {
                queryParams: {
                  section: 'subscription',
                  mode: 'edit',
                },
              });
              break;

            case 'contact':
              this.openContactDialog('pricing page');
              break;

            case 'try':
              let workflow = '';
              switch (true) {
                case plans.business.includes(metadata.plan):
                  workflow = `/account/workflow/team/create?purpose=Business&returnTo=${returnUrl}`;
                  break;

                case plans.education.includes(metadata.plan):
                  workflow = `/account/workflow/team/create?purpose=Education&returnTo=${returnUrl}`;
                  break;

                default:
                  workflow = 'account/workflow';
              }

              this.router.navigateByUrl(workflow);
              break;

            default:
              // Do nothing
              break;
          }
        } else {
          this.pendoService.track('Pricing - Exit');
        }
      });
  }

  openContactDialog(campaign: string = '') {
    // get current organization: optional
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    const organizationId = !isNil(organization) ? `${organization.id}` : '';
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);
    const contact = this.getContactProfileParams(profile);

    // Note: craft sandbox will fail to load because it's not secure.
    // To test, open chrome://flags, enable for "Insecure origins treated as secure", and add the craft-sandbox domain
    const contactHosts = {
      // [EnvironmentDeploy.Local]: `http://localhost:8080`, // Uncomment for local dev
      [EnvironmentDeploy.Local]: `http://craft-sandbox.biodigital.local`,
      [EnvironmentDeploy.QA]: `http://craft-sandbox.biodigital.local`,
      [EnvironmentDeploy.Staging]: `https://www.biodigital.com`,
      [EnvironmentDeploy.Production]: `https://www.biodigital.com`,
    };
    const contactHost = environment.native
      ? contactHosts[EnvironmentDeploy.Production]
      : contactHosts[environment.deploy];

    if (environment.native) {
      // native: contact us
      const contactQueryString = this.serializeQueryParams({
        ...contact,
        organizationId: organizationId,
        utm_source: 'human-mobile',
        utm_campaign: campaign,
      });
      const url = `${contactHost}/contact?${contactQueryString}`;

      return window.open(url, '_blank');
    } else {
      // web: contact us
      const contactQueryString = this.serializeQueryParams({
        ...contact,
        organizationId: organizationId,
        utm_source: 'human-web',
        utm_campaign: campaign,
      });

      const url = `${contactHost}/headless/contact-us?${contactQueryString}`;

      const contactOptions = {
        id: DialogIds.ContactDialog,
        ...fullScreenConfig,
        data: <IframeDialogData>{
          title: 'Contact Us',
          url: url,
        },
      };

      return this.dialog.open(IframeDialogComponent, contactOptions);
    }
  }

  openPremiumContentDialog() {
    const upgradeEnabled = environment.native || !this.isMobile;

    const bodyText = upgradeEnabled
      ? 'This is premium content. Upgrade to a Premium Plan for unlimited access.'
      : 'This is premium content. Visit us on desktop to upgrade to a premium plan.';

    return this.openConfirmDialog({
      id: DialogIds.PremiumContentDialog,
      data: {
        titleText: 'Premium Content',
        bodyText: bodyText,
        confirmText: 'Upgrade Now',
        confirmEnabled: upgradeEnabled,
        cancelText: 'OK',
        confirmColor: 'warn',
      },
    });
  }

  closeAllDialogs() {
    this.dialog.closeAll();
  }

  private getContactProfileParams(profile: UserProfileModel): any {
    // user fields
    const { first_name, last_name, email } = profile;
    const organization = get(profile, 'organizations[0].name', '');
    const groupAttributes = get(profile, 'organizations[0].group_attributes', []);

    // user attributes
    const accountSettingsMap = createAccountSettingsMap(profile.account_settings);
    const purpose: string = get(accountSettingsMap, 'account_purpose.value', '');
    const industry: string = get(accountSettingsMap, 'account_field.value', '');
    const field: string = get(accountSettingsMap, 'account_field.value', '');
    const role: string = get(accountSettingsMap, 'account_role.value', '');

    // company attributes
    const industrySizeAttribute: OrganizationAttributeModel =
      this.organizationsService.getOrganizationAttribute(
        groupAttributes,
        'account_settings',
        'account_company_size',
      );
    const websiteAttribute: OrganizationAttributeModel =
      this.organizationsService.getOrganizationAttribute(
        groupAttributes,
        'account_settings',
        'account_company_website',
      );
    const industry_size = industrySizeAttribute ? industrySizeAttribute.value : '';
    const website = websiteAttribute ? websiteAttribute.value : '';

    return {
      // user
      first_name,
      last_name,
      email,
      // company
      company: organization,
      website,
      // user demographics
      purpose,
      role,
      program_type: field,
      program_size: industry_size,
      // special mapping for Education
      industry: purpose === AccountPurposeEnum.Education ? 'Academia' : industry,
    };
  }

  private serializeQueryParams(params: Record<string, any>): string {
    return Object.entries(params)
      .map(([key, value]) => {
        const serializedValue = typeof value !== 'string' ? JSON.stringify(value) : value;
        const encodedURIValue = encodeURIComponent(serializedValue);
        return `${key}=${encodedURIValue}`;
      })
      .join('&');
  }
}
